import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICtaForm, ICustomCta, ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { DurationPipe } from '@core/pipes/duration.pipe';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { isNil } from 'lodash';
import { EExecutionType } from '../deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import {
  EXECUTION_TYPE_ORDER,
  TCtaSummaryDeliverable,
  TCtaSummaryExecution,
  TCtaSummaryExecutionRow,
} from './cta-summary-panel.interface';

@Component({
  imports: [
    EditableTitleComponent,
    IconComponent,
    MatButtonModule,
    MatExpansionModule,
    MatTooltipModule,
    NumberSuffixPipe,
    SocialBadgeComponent,
    TranslateModule,
  ],
  providers: [DatePipe, DurationPipe],
  selector: 'app-cta-summary-panel',
  templateUrl: './cta-summary-panel.component.html',
})
export class CtaSummaryPanelComponent {
  public readonly addDeliverablesForCta = output<string>();
  public readonly addExecutionsForCta = output<string>();
  public readonly addKpisForCta = output<string>();

  public readonly creatorId = input.required<string>();
  public readonly cta = input.required<FormGroup<ICtaForm>['value']>();
  public readonly getExistingDeliverablesCount = input.required<(ctaId: string, creatorId: string) => number>();
  public readonly getExistingExecutionsCount = input.required<(ctaId: string, creatorId: string) => number | null>();
  public readonly getExistingKpisCount = input.required<(ctaId: string, creatorId: string) => number>();
  public readonly hideShareStatusIcon = input<boolean>(false);
  public readonly ctaShareCount = input<number>(0);
  public readonly hasCustomValue = input<boolean>(false);
  public readonly panelClass = input<string>('');

  public readonly expansionPanel = viewChild.required(MatExpansionPanel);
  public readonly isExpanded = signal<boolean>(false);

  public readonly EExecutionType = EExecutionType;

  public readonly fullPanelClass = computed(() =>
    this.panelClass() ? `cta-summary-expansion-panel ${this.panelClass()}` : 'cta-summary-expansion-panel',
  );

  public readonly ctaTag = computed(() => {
    if (this.hideShareStatusIcon()) return null;

    const isGlobal = this.cta().isAllCreators;
    const isShared = !isGlobal && this.ctaShareCount() > 1;
    const customized = this.hasCustomValue();

    if (isGlobal) {
      return customized ? 'globalCustomized' : 'global';
    }

    if (isShared) {
      return customized ? 'sharedCustomized' : 'shared';
    }

    return 'individual';
  });

  public readonly deliverables = computed(() => {
    const raw = (this.cta().deliverables as TCtaSummaryDeliverable[]) ?? [];

    return raw
      .filter((d) => this.isIncludedForCreator(d))
      .map((d) => ({
        ...d,
        displayName: d.name || (this.translate.instant(`shared.deliverableTypes.${d.type}`) as string),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  });

  public readonly executions = computed((): TCtaSummaryExecutionRow[] => {
    const raw = ((this.cta().executions as TCtaSummaryExecution[]) ?? []).filter((exec) =>
      this.isIncludedForCreator(exec),
    );

    // Pre-compute sort keys — O(n)
    const keyed = raw.map((exec) => ({
      dateTs: exec.executionDate ? new Date(exec.executionDate).getTime() : Infinity,
      exec,
      timeTs: exec.executionTime ? new Date(exec.executionTime).getTime() : Infinity,
      typeOrder: EXECUTION_TYPE_ORDER[exec.executionType!] ?? 99,
    }));

    keyed.sort((a, b) => {
      if (a.typeOrder !== b.typeOrder) return a.typeOrder - b.typeOrder;

      if (a.exec.executionType === EExecutionType.unique) {
        if (a.dateTs !== b.dateTs) {
          return a.dateTs - b.dateTs;
        }

        if (a.timeTs !== b.timeTs) {
          return a.timeTs - b.timeTs;
        }

        return 0;
      }

      if (a.exec.executionType === EExecutionType.undesignated) {
        const da = a.exec.duration ?? Infinity;
        const db = b.exec.duration ?? Infinity;

        return da === db ? 0 : da - db;
      }

      return 0;
    });

    return keyed.map(({ exec }) => ({
      ...exec,
      durationLabel: this.buildExecutionDuration(exec),
      label: this.buildExecutionLabel(exec),
    }));
  });

  public readonly allKpis = computed(() => {
    const kpis = (this.cta().executions as TCtaSummaryExecution[])?.flatMap((exec) => exec.expectedKpis ?? []) ?? [];

    return kpis
      .filter((kpi) => this.isIncludedForCreator(kpi))
      .map((kpi) => ({
        ...kpi,
        translatedName: this.translate.instant(`shared.expectedKpis.${kpi.kpi}`) as string,
      }))
      .sort((a, b) => a.translatedName.localeCompare(b.translatedName));
  });

  private readonly datePipe = inject(DatePipe);
  private readonly durationPipe = inject(DurationPipe);
  private readonly translate = inject(TranslateService);

  private buildExecutionLabel(execution: TCtaSummaryExecution): string {
    switch (execution.executionType) {
      case EExecutionType.unique: {
        const date = execution.executionDate
          ? (this.datePipe.transform(execution.executionDate, 'dd.MM.yyyy') ?? '')
          : '';
        const time = execution.executionTime
          ? `, ${this.datePipe.transform(execution.executionTime, 'HH:mm') ?? ''}`
          : '';

        return this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.ctaSummary.scheduledAt', {
          date: `${date}${time}`,
        });
      }
      case EExecutionType.undesignated:
        return this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.ctaSummary.repeats', {
          count: execution.executionCountTarget ?? 1,
        });
      case EExecutionType.always:
        return this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.ctaSummary.always');
      default:
        return '';
    }
  }

  /**
   * Mirrors `CreatorsAndCtasService`'s exclusivity/deletion checks used for the count badges,
   * so the expanded body lists the same items that were counted.
   */
  private isIncludedForCreator(item: ICustomCta): boolean {
    const creatorId = this.creatorId();
    const customCtaValues = item.customCtaValues as ICustomCtaValue[] | null | undefined;
    const isDeleted = customCtaValues?.some((value) => value.creatorId === creatorId && value.valueName === null);

    return !isDeleted && (!item.exclusiveTo || item.exclusiveTo === creatorId);
  }

  private buildExecutionDuration(execution: TCtaSummaryExecution): string | null {
    if (isNil(execution.duration) || execution.executionType === EExecutionType.always) {
      return null;
    }

    return this.durationPipe.transform(execution.duration);
  }
}
